import {
  AccessControlViewModel,
  AccessSwitchViewModel,
  ChoiceControlText,
  ChoiceControlViewModel,
  ChoiceOptionViewModel,
  ChoiceRowViewModel,
  TranslatedText,
} from '../models/ai-hub.models';

export interface AccessControlInput {
  iqBoostOn: boolean;
  isAdmin: boolean;
  orgAvailable: boolean;
  userEnabled: boolean;
  orgGateOpen: boolean;
  onSummary?: TranslatedText;
}

export interface AnonymizerControlInput {
  iqBoostOn: boolean;
  isAdmin: boolean;
  orgRequired: boolean;
  userEnabled: boolean;
  flagEnabled: boolean;
}

const ORG_LABEL = 'aiHub.access.availableToUsers';
const MINE_LABEL = 'aiHub.access.enabled';
const ANON_ORG_LABEL = 'aiHub.general.anonymizer.requiredForEveryone';
const ANON_MINE_LABEL = 'aiHub.general.anonymizer.useForMyAccount';
const MANAGED = { key: 'aiHub.access.managedByOrganization' };
const TURN_ON_IQ_BOOST = { key: 'aiHub.access.turnOnIqBoostFirst' };

function iqBoostOffCaption(isAdmin: boolean): TranslatedText {
  return { key: isAdmin ? 'aiHub.access.iqBoostOffAdmin' : 'aiHub.access.iqBoostOffUser' };
}

function lockedSwitch(labelKey: string, caption: TranslatedText, checked = false): AccessSwitchViewModel {
  return { labelKey, caption, checked, disabled: true, locked: true };
}

function orgRow(isAdmin: boolean, row: AccessSwitchViewModel): AccessSwitchViewModel | null {
  return isAdmin ? row : null;
}

export function buildAccessControl(input: AccessControlInput): AccessControlViewModel {
  const { iqBoostOn, isAdmin, orgAvailable, userEnabled, orgGateOpen, onSummary } = input;

  if (!orgGateOpen) {
    return {
      org: orgRow(isAdmin, { labelKey: ORG_LABEL, caption: MANAGED, checked: false, disabled: true, locked: false }),
      mine: lockedSwitch(MINE_LABEL, MANAGED),
    };
  }

  const org = orgRow(isAdmin, {
    labelKey: ORG_LABEL,
    caption: !iqBoostOn ? TURN_ON_IQ_BOOST : { key: orgAvailable ? 'aiHub.access.usersCanTurnOn' : 'aiHub.access.hiddenFromUsers' },
    checked: iqBoostOn && orgAvailable,
    disabled: !iqBoostOn,
    locked: false,
  });

  if (!iqBoostOn) return { org, mine: lockedSwitch(MINE_LABEL, iqBoostOffCaption(isAdmin)) };
  if (!orgAvailable) {
    return { org, mine: lockedSwitch(MINE_LABEL, { key: isAdmin ? 'aiHub.access.offForOrganization' : 'aiHub.access.notMadeAvailable' }) };
  }

  return {
    org,
    mine: {
      labelKey: MINE_LABEL,
      caption: userEnabled ? (onSummary ?? { key: 'aiHub.access.onInAccount' }) : { key: 'aiHub.access.offInAccount' },
      checked: userEnabled,
      disabled: false,
      locked: false,
    },
  };
}

export function buildAnonymizerControl(input: AnonymizerControlInput): AccessControlViewModel {
  const { iqBoostOn, isAdmin, orgRequired, userEnabled, flagEnabled } = input;

  if (!flagEnabled) {
    return {
      org: orgRow(isAdmin, { labelKey: ANON_ORG_LABEL, caption: MANAGED, checked: true, disabled: true, locked: false }),
      mine: lockedSwitch(ANON_MINE_LABEL, MANAGED, true),
    };
  }

  if (!iqBoostOn) {
    return {
      org: orgRow(isAdmin, { labelKey: ANON_ORG_LABEL, caption: TURN_ON_IQ_BOOST, checked: false, disabled: true, locked: false }),
      mine: lockedSwitch(ANON_MINE_LABEL, iqBoostOffCaption(isAdmin)),
    };
  }

  const org = orgRow(isAdmin, {
    labelKey: ANON_ORG_LABEL,
    caption: { key: orgRequired ? 'aiHub.general.anonymizer.requiredCaption' : 'aiHub.general.anonymizer.notRequiredCaption' },
    checked: orgRequired,
    disabled: false,
    locked: false,
  });

  if (orgRequired) {
    return { org, mine: lockedSwitch(ANON_MINE_LABEL, { key: 'aiHub.general.anonymizer.requiredByOrganization' }, true) };
  }

  return {
    org,
    mine: {
      labelKey: ANON_MINE_LABEL,
      caption: { key: userEnabled ? 'aiHub.general.anonymizer.userOnCaption' : 'aiHub.general.anonymizer.userOffCaption' },
      checked: userEnabled,
      disabled: false,
      locked: false,
    },
  };
}

export interface ChoiceControlInput {
  iqBoostOn: boolean;
  isAdmin: boolean;
  flagEnabled: boolean;
  orgOpen: boolean;
  userOpen: boolean;
  text: ChoiceControlText;
}

function choiceOptions(text: ChoiceControlText): readonly ChoiceOptionViewModel[] {
  return [
    { open: false, labelKey: text.restrictiveLabelKey },
    { open: true, labelKey: text.openLabelKey },
  ];
}

function lockedChoice(selectedOpen: boolean, caption: TranslatedText): ChoiceRowViewModel {
  return { selectedOpen, disabled: true, locked: true, caption };
}

export function buildChoiceControl(input: ChoiceControlInput): ChoiceControlViewModel {
  const { iqBoostOn, isAdmin, flagEnabled, orgOpen, userOpen, text } = input;
  const options = choiceOptions(text);
  const appliedOpen = orgOpen && userOpen;
  const organizationRow = (row: ChoiceRowViewModel): ChoiceRowViewModel | null => (isAdmin ? row : null);

  if (!flagEnabled) {
    return {
      options,
      org: organizationRow({ selectedOpen: orgOpen, disabled: true, locked: false, caption: MANAGED }),
      mine: lockedChoice(appliedOpen, MANAGED),
    };
  }

  if (!iqBoostOn) {
    return {
      options,
      org: organizationRow({ selectedOpen: orgOpen, disabled: true, locked: false, caption: TURN_ON_IQ_BOOST }),
      mine: lockedChoice(appliedOpen, iqBoostOffCaption(isAdmin)),
    };
  }

  const org = organizationRow({ selectedOpen: orgOpen, disabled: false, locked: false, caption: null });

  if (!orgOpen) return { options, org, mine: lockedChoice(false, { key: text.requiredCaptionKey }) };

  return {
    options,
    org,
    mine: { selectedOpen: userOpen, disabled: false, locked: false, caption: { key: text.chooseCaptionKey } },
  };
}
